import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';
import { BOOKS } from './data/books';
import { syncExerciseEntry, trackSocialEvent } from '../../shared/dbService';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const WORDS_PER_PAGE = 180;
const PUBLISHER = 'Sense Lifting';
const BOOK_YEARS = {};
BOOKS.forEach((b, i) => { BOOK_YEARS[b.id] = 2022 + (i % 3); });

const EMOTION_OPTIONS = ['😮', '🤔', '😢', '😍', '😮‍💨', '😴'];
const TIME_OPTIONS = ['Flew by', 'Normal', 'Slow'];
const GENRES = ['All', 'Sci-Fi', 'Thriller', 'Romance'];
const LANG_MAP = { en: 'ENGLISH', nl: 'NEDERLANDS', de: 'DEUTSCH' };

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
function paginateChapter(content) {
  const words = content.split(/\s+/).filter(Boolean);
  const pages = [];
  for (let i = 0; i < words.length; i += WORDS_PER_PAGE) {
    pages.push(words.slice(i, i + WORDS_PER_PAGE).join(' '));
  }
  return pages.length ? pages : [''];
}

function buildPages(book) {
  // page 0 = title page, then chapter pages
  const pages = [{ type: 'title' }];
  book.chapters.forEach((ch, ci) => {
    const textPages = paginateChapter(ch.content);
    textPages.forEach((text, pi) => {
      pages.push({ type: 'content', chapterIndex: ci, chapterTitle: ch.title, pageInChapter: pi, text });
    });
  });
  return pages;
}

function getReflections() {
  try { return JSON.parse(localStorage.getItem('sl_read_reflections') || '[]'); } catch { return []; }
}

function saveReflection(entry) {
  const arr = getReflections();
  const idx = arr.findIndex(r => r.bookId === entry.bookId);
  if (idx >= 0) arr[idx] = entry; else arr.push(entry);
  localStorage.setItem('sl_read_reflections', JSON.stringify(arr));
}

function getReflectionForBook(bookId) {
  return getReflections().find(r => r.bookId === bookId) || null;
}

function fmtTime(sec) {
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function toRoman(n) {
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ['M','CM','D','CD','C','XC','L','XL','X','IX','V','IV','I'];
  let out = '';
  vals.forEach((v, i) => { while (n >= v) { out += syms[i]; n -= v; } });
  return out;
}

// ─────────────────────────────────────────────
// CSS KEYFRAMES injection
// ─────────────────────────────────────────────
const KEYFRAMES = `
@keyframes flip3DForward {
  0%   { transform: perspective(1200px) rotateY(0deg); opacity:1; }
  50%  { transform: perspective(1200px) rotateY(-90deg); opacity:0.6; }
  100% { transform: perspective(1200px) rotateY(0deg); opacity:1; }
}
@keyframes flip3DBack {
  0%   { transform: perspective(1200px) rotateY(0deg); opacity:1; }
  50%  { transform: perspective(1200px) rotateY(90deg); opacity:0.6; }
  100% { transform: perspective(1200px) rotateY(0deg); opacity:1; }
}
@keyframes slideUp {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
`;

function injectStyles() {
  if (document.getElementById('readtab-styles')) return;
  const el = document.createElement('style');
  el.id = 'readtab-styles';
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

// ─────────────────────────────────────────────
// STYLES (inline objects)
// ─────────────────────────────────────────────
const PARCHMENT = 'linear-gradient(160deg, #f7edd8 0%, #ecddc4 40%, #e8d4b0 80%, #e0caa0 100%)';

const ss = {
  // wrapper
  root: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: _T.bg, color: C.white, fontFamily: 'Georgia, serif', paddingBottom: 24 },

  // filter row
  filterRow: { display: 'flex', gap: 8, padding: '14px 16px 4px', flexWrap: 'wrap' },
  pill: (active) => ({
    padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: 'none',
    background: active ? 'linear-gradient(135deg,#c9a84c,#e0c060)' : _T.bg3,
    color: active ? '#1a0e00' : C.silver, fontWeight: active ? 700 : 400, transition: 'all .2s'
  }),

  // lang section
  langSection: { marginBottom: 28 },
  langLabel: { fontSize: 11, letterSpacing: 3, color: C.silver2, padding: '0 16px 8px', textTransform: 'uppercase' },
  shelfRow: { display: 'flex', gap: 18, padding: '4px 16px 12px', overflowX: 'auto', scrollbarWidth: 'none' },

  // book card
  bookCard: (coverColor, coverColor2) => ({
    flexShrink: 0, width: 130, height: 200, borderRadius: '2px 6px 6px 2px', cursor: 'pointer', position: 'relative',
    background: `linear-gradient(160deg, ${coverColor} 0%, ${coverColor2} 100%)`,
    boxShadow: '4px 6px 18px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)',
    display: 'flex', overflow: 'hidden', transition: 'transform .2s',
    transform: 'perspective(600px) rotateY(0deg)',
  }),
  spine: { width: 14, flexShrink: 0, background: 'rgba(0,0,0,0.35)', borderRadius: '2px 0 0 2px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  coverFace: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '14px 8px 10px' },
  coverEmoji: { fontSize: 38, lineHeight: 1 },
  coverTitle: { fontSize: 14, fontWeight: 700, fontFamily: 'Georgia,serif', color: '#fff', textAlign: 'center', lineHeight: 1.25, textShadow: '0 1px 4px rgba(0,0,0,0.6)' },
  coverAuthor: { fontSize: 10, color: 'rgba(255,255,255,0.75)', textAlign: 'center', letterSpacing: 0.3 },
  coverGenre: { fontSize: 9, color: 'rgba(255,255,255,0.55)', letterSpacing: 1.5, textTransform: 'uppercase', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 5, width: '100%', textAlign: 'center' },
  readBadge: { position: 'absolute', top: 7, right: 7, width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg,#c9a84c,#e0c060)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#1a0e00', fontWeight: 900, boxShadow: '0 2px 6px rgba(0,0,0,0.4)' },

  // lightbox modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'flex-end' },
  modalSheet: { width: '100%', maxHeight: '85vh', overflowY: 'auto', background: _T.bg2, borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', boxSizing: 'border-box', animation: 'slideUp .3s ease' },
  modalTitle: { fontSize: 24, fontWeight: 700, fontFamily: 'Georgia,serif', color: C.white, marginBottom: 4 },
  modalAuthor: { fontSize: 14, color: C.silver, marginBottom: 12 },
  badgeRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  badge: { padding: '4px 11px', borderRadius: 12, background: _T.bg3, fontSize: 12, color: C.silver, border: `1px solid ${_T.bg4}` },
  modalDesc: { fontSize: 14, lineHeight: 1.8, color: C.silver, marginBottom: 20 },
  startBtn: { width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#2e7d32,#c9a84c)', color: '#fff', fontSize: 16, fontWeight: 700, letterSpacing: 0.5 },
  closeBtn: { position: 'absolute', top: 16, right: 16, background: _T.bg3, border: 'none', color: C.silver, borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },

  // reader
  readerWrap: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#1a0e00', overflow: 'hidden' },
  readerTopBar: { display: 'flex', alignItems: 'center', padding: '10px 14px', background: 'rgba(0,0,0,0.5)', zIndex: 10, gap: 10 },
  backBtn: { background: 'none', border: 'none', color: '#c9a84c', fontSize: 22, cursor: 'pointer', padding: '2px 8px' },
  readerBookTitle: { flex: 1, fontSize: 13, fontFamily: 'Georgia,serif', color: 'rgba(255,255,255,0.7)', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  timerBadge: { fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.5 },
  chapterDots: { display: 'flex', gap: 5, justifyContent: 'center', padding: '4px 0' },
  chDot: (active) => ({ width: 6, height: 6, borderRadius: '50%', background: active ? '#c9a84c' : 'rgba(255,255,255,0.2)' }),

  // page
  pageOuter: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px', overflow: 'hidden' },
  pageInner: { width: '100%', maxWidth: 520, minHeight: 420, background: PARCHMENT, borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.55), inset 4px 0 10px rgba(0,0,0,0.15)', padding: '28px 26px 36px 34px', boxSizing: 'border-box', position: 'relative', overflowY: 'auto', maxHeight: 'calc(100vh - 160px)' },
  bindingShadow: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 12, background: 'linear-gradient(to right, rgba(0,0,0,0.25), transparent)', borderRadius: '4px 0 0 4px', pointerEvents: 'none' },
  pageNum: { textAlign: 'center', fontSize: 12, color: '#8b6914', marginTop: 20, letterSpacing: 1 },

  // title page
  titlePageWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', gap: 10 },
  titlePageEmoji: { fontSize: 52, marginBottom: 4 },
  titlePageTitle: { fontFamily: 'Georgia,serif', fontSize: 28, fontWeight: 700, color: '#2a1505', lineHeight: 1.2 },
  titlePageOrnament: { fontSize: 22, color: '#8b6914', margin: '8px 0' },
  titlePageAuthor: { fontFamily: 'Georgia,serif', fontSize: 16, color: '#5a2800', letterSpacing: 1 },
  titlePagePublisher: { fontSize: 12, color: '#8b6914', letterSpacing: 2, textTransform: 'uppercase', marginTop: 18 },
  titlePageYear: { fontSize: 12, color: '#a07840' },

  // chapter header
  chapterLabel: { fontSize: 11, letterSpacing: 5, color: '#8b6914', textTransform: 'uppercase', fontFamily: 'Georgia,serif', marginBottom: 6 },
  chapterTitle: { fontFamily: 'Georgia,serif', fontSize: 22, fontWeight: 700, color: '#2a1505', marginBottom: 18, lineHeight: 1.3 },
  bodyText: { fontFamily: 'Georgia,serif', fontSize: 17, lineHeight: 1.95, color: '#2a1505' },
  dropCap: { float: 'left', fontSize: 72, lineHeight: 0.8, fontFamily: 'Georgia,serif', fontWeight: 700, color: '#5a2800', marginRight: 6, marginTop: 8, paddingBottom: 8 },

  // bottom nav bar
  readerBottomBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', background: 'rgba(0,0,0,0.5)' },
  navBtn: (disabled) => ({ background: disabled ? 'rgba(255,255,255,0.05)' : 'rgba(201,168,76,0.15)', border: `1px solid ${disabled ? 'rgba(255,255,255,0.08)' : 'rgba(201,168,76,0.35)'}`, color: disabled ? 'rgba(255,255,255,0.2)' : '#c9a84c', borderRadius: 10, padding: '8px 18px', fontSize: 20, cursor: disabled ? 'default' : 'pointer' }),
  pageDots: { display: 'flex', gap: 5 },
  pageDot: (active) => ({ width: 6, height: 6, borderRadius: '50%', background: active ? '#c9a84c' : 'rgba(255,255,255,0.2)', transition: 'background .2s' }),

  // reflection
  reflectionWrap: { minHeight: '100vh', background: _T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 20px' },
  reflectionTitle: { fontSize: 22, fontFamily: 'Georgia,serif', color: '#c9a84c', textAlign: 'center', marginBottom: 6 },
  reflectionBook: { fontSize: 17, fontFamily: 'Georgia,serif', color: C.white, textAlign: 'center', marginBottom: 28 },
  reflectionLabel: { fontSize: 13, color: C.silver, marginBottom: 8, letterSpacing: 0.3 },
  reflectionTextarea: { width: '100%', minHeight: 90, background: _T.bg3, border: `1px solid ${_T.bg4}`, borderRadius: 10, color: C.white, fontSize: 14, fontFamily: 'Georgia,serif', padding: 12, boxSizing: 'border-box', resize: 'vertical', marginBottom: 20, lineHeight: 1.7 },
  emojiRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 },
  emojiBtn: (active) => ({ fontSize: 26, background: active ? 'rgba(201,168,76,0.25)' : _T.bg3, border: `2px solid ${active ? '#c9a84c' : 'transparent'}`, borderRadius: 10, padding: '6px 10px', cursor: 'pointer', transition: 'all .15s' }),
  timeRow: { display: 'flex', gap: 10, marginBottom: 28 },
  timeBtn: (active) => ({ flex: 1, padding: '10px 6px', borderRadius: 10, border: `2px solid ${active ? '#c9a84c' : _T.bg4}`, background: active ? 'rgba(201,168,76,0.15)' : _T.bg3, color: active ? '#c9a84c' : C.silver, fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400, transition: 'all .15s', textAlign: 'center' }),
  submitBtn: { width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#2e7d32,#c9a84c)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5 },

  // read-again stats in modal
  statsBox: { background: _T.bg3, borderRadius: 12, padding: '14px 16px', marginBottom: 16, borderLeft: '3px solid #c9a84c' },
  statsHeader: { fontSize: 14, color: '#c9a84c', fontWeight: 700, marginBottom: 8 },
  statsRow: { fontSize: 13, color: C.silver, lineHeight: 1.8 },
};

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

// --- Book Card ---
function BookCard({ book, onOpen }) {
  const reflection = getReflectionForBook(book.id);
  const isRead = !!reflection;

  return (
    <div
      style={ss.bookCard(book.coverColor || '#1a0e00', book.coverColor2 || '#2a1a05')}
      onClick={() => onOpen(book)}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'perspective(600px) rotateY(-8deg) translateY(-4px)';
        e.currentTarget.style.boxShadow = '8px 12px 28px rgba(0,0,0,0.65)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'perspective(600px) rotateY(0deg) translateY(0)';
        e.currentTarget.style.boxShadow = '4px 6px 18px rgba(0,0,0,0.55)';
      }}
    >
      <div style={ss.spine} />
      <div style={ss.coverFace}>
        <div style={ss.coverEmoji}>{book.cover}</div>
        <div style={ss.coverTitle}>{book.title}</div>
        <div style={ss.coverAuthor}>{book.author}</div>
        <div style={ss.coverGenre}>{book.genre}</div>
      </div>
      {isRead && <div style={ss.readBadge}>✓</div>}
    </div>
  );
}

// --- Back Cover Lightbox ---
function BookModal({ book, onClose, onStartReading }) {
  if (!book) return null;
  const reflection = getReflectionForBook(book.id);
  const isRead = !!reflection;
  const year = BOOK_YEARS[book.id] || 2023;

  return (
    <div style={ss.modalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...ss.modalSheet, position: 'relative' }}>
        <button style={ss.closeBtn} onClick={onClose}>×</button>

        {isRead && (
          <div style={{ ...ss.statsBox, marginBottom: 16 }}>
            <div style={ss.statsHeader}>✓ You read this book</div>
            <div style={ss.statsRow}>
              Read in {reflection.readingMinutes} minute{reflection.readingMinutes !== 1 ? 's' : ''} · {new Date(reflection.completedAt).toLocaleDateString()}
            </div>
          </div>
        )}

        <div style={ss.modalTitle}>{book.title}</div>
        <div style={ss.modalAuthor}>{book.author}</div>

        <div style={ss.badgeRow}>
          <span style={ss.badge}>{book.genre}</span>
          <span style={ss.badge}>{book.theme}</span>
          <span style={ss.badge}>{year}</span>
          <span style={ss.badge}>{PUBLISHER}</span>
        </div>

        <div style={ss.modalDesc}>{book.description}</div>

        {isRead && reflection && (
          <div style={{ ...ss.statsBox, marginBottom: 20 }}>
            <div style={ss.statsHeader}>Your Reflection</div>
            {reflection.summary && (
              <div style={{ ...ss.statsRow, marginBottom: 6 }}>📝 {reflection.summary}</div>
            )}
            {reflection.emotions?.length > 0 && (
              <div style={{ ...ss.statsRow, marginBottom: 6 }}>Emotions: {reflection.emotions.join(' ')}</div>
            )}
            {reflection.timeFelt && (
              <div style={ss.statsRow}>Time felt: {reflection.timeFelt}</div>
            )}
          </div>
        )}

        <button style={ss.startBtn} onClick={() => onStartReading(book)}>
          {isRead ? 'Read Again' : 'Start Reading'}
        </button>
      </div>
    </div>
  );
}

// --- Title Page ---
function TitlePage({ book }) {
  const year = BOOK_YEARS[book.id] || 2023;
  return (
    <div style={ss.titlePageWrap}>
      <div style={ss.titlePageEmoji}>{book.cover}</div>
      <div style={ss.titlePageOrnament}>❦</div>
      <div style={ss.titlePageTitle}>{book.title}</div>
      <div style={ss.titlePageOrnament}>✦</div>
      <div style={ss.titlePageAuthor}>{book.author}</div>
      <div style={{ ...ss.titlePagePublisher, marginTop: 28 }}>{PUBLISHER}</div>
      <div style={ss.titlePageYear}>{year}</div>
    </div>
  );
}

// --- Content Page ---
function ContentPage({ page, pageNumber }) {
  const isFirstPageOfChapter = page.pageInChapter === 0;
  const text = page.text || '';
  const firstChar = isFirstPageOfChapter && text.length > 0 ? text[0] : null;
  const restText = isFirstPageOfChapter && text.length > 0 ? text.slice(1) : text;
  const chapterNum = toRoman(page.chapterIndex + 1);

  return (
    <div style={{ position: 'relative' }}>
      {isFirstPageOfChapter && (
        <>
          <div style={ss.chapterLabel}>Chapter {chapterNum}</div>
          <div style={ss.chapterTitle}>{page.chapterTitle}</div>
        </>
      )}
      <div style={ss.bodyText}>
        {firstChar && <span style={ss.dropCap}>{firstChar}</span>}
        {restText}
      </div>
      <div style={ss.pageNum}>— {pageNumber} —</div>
    </div>
  );
}

// --- Reflection Form ---
function ReflectionForm({ book, readingMinutes, onDone }) {
  const [summary, setSummary] = useState('');
  const [emotions, setEmotions] = useState([]);
  const [timeFelt, setTimeFelt] = useState('');

  function toggleEmotion(e) {
    setEmotions(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  }

  function handleSubmit() {
    const entry = {
      bookId: book.id,
      readingMinutes,
      summary,
      emotions,
      timeFelt,
      completedAt: new Date().toISOString(),
    };
    saveReflection(entry);
    syncExerciseEntry({ type: 'book_read', category: 'cognitive', duration: readingMinutes });
    trackSocialEvent('book_read', { weight: 5 });
    onDone();
  }

  return (
    <div style={ss.reflectionWrap}>
      <div style={ss.reflectionTitle}>✦ You finished:</div>
      <div style={ss.reflectionBook}>{book.title}</div>

      <div style={{ width: '100%', maxWidth: 480 }}>
        <div style={ss.reflectionLabel}>Write a brief summary of the story</div>
        <textarea
          style={ss.reflectionTextarea}
          value={summary}
          onChange={e => setSummary(e.target.value)}
          placeholder="In a few sentences, what happened?"
        />

        <div style={ss.reflectionLabel}>What emotions did you feel?</div>
        <div style={ss.emojiRow}>
          {EMOTION_OPTIONS.map(em => (
            <button key={em} style={ss.emojiBtn(emotions.includes(em))} onClick={() => toggleEmotion(em)}>
              {em}
            </button>
          ))}
        </div>

        <div style={ss.reflectionLabel}>How did time feel while reading?</div>
        <div style={ss.timeRow}>
          {TIME_OPTIONS.map(t => (
            <button key={t} style={ss.timeBtn(timeFelt === t)} onClick={() => setTimeFelt(t)}>
              {t}
            </button>
          ))}
        </div>

        <button style={ss.submitBtn} onClick={handleSubmit}>
          Save my reflection →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// READER VIEW
// ─────────────────────────────────────────────
function ReaderView({ book, onClose }) {
  const pages = useMemo(() => buildPages(book), [book]);
  const [pageIndex, setPageIndex] = useState(0);
  const [flipPhase, setFlipPhase] = useState(null); // 'out' | 'in' | null
  const [flipDir, setFlipDir] = useState('forward'); // 'forward' | 'back'
  const [seconds, setSeconds] = useState(0);
  const [showReflection, setShowReflection] = useState(false);
  const touchStart = useRef(null);
  const timerRef = useRef(null);
  const pendingPage = useRef(pageIndex);

  // Reading timer
  useEffect(() => {
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // 3D page flip: two-phase animation (out → change content → in)
  const goToPage = useCallback((newIndex, dir) => {
    if (newIndex < 0 || newIndex >= pages.length) return;
    pendingPage.current = newIndex;
    setFlipDir(dir);
    setFlipPhase('out');
    setTimeout(() => {
      setPageIndex(newIndex);
      setFlipPhase('in');
      setTimeout(() => setFlipPhase(null), 200);
    }, 200);
  }, [pages.length]);

  function nextPage() {
    if (pageIndex < pages.length - 1) {
      goToPage(pageIndex + 1, 'forward');
    } else {
      // Last page of last chapter → show reflection form
      setShowReflection(true);
    }
  }

  function prevPage() {
    if (pageIndex > 0) goToPage(pageIndex - 1, 'back');
  }

  // Swipe support
  function onTouchStart(e) { touchStart.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStart.current === null) return;
    const delta = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) { delta > 0 ? nextPage() : prevPage(); }
    touchStart.current = null;
  }

  const currentPage = pages[pageIndex];
  const currentChapter = currentPage?.type === 'content' ? currentPage.chapterIndex : -1;
  const readingMinutes = Math.max(1, Math.round(seconds / 60));

  // Page flip animation style
  const animStyle = flipPhase
    ? { animation: `${flipDir === 'forward' ? 'flip3DForward' : 'flip3DBack'} 0.2s ease forwards` }
    : {};

  if (showReflection) {
    return <ReflectionForm book={book} readingMinutes={readingMinutes} onDone={onClose} />;
  }

  return (
    <div
      style={ss.readerWrap}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar: back, title, timer */}
      <div style={ss.readerTopBar}>
        <button style={ss.backBtn} onClick={onClose}>‹</button>
        <div style={ss.readerBookTitle}>{book.title}</div>
        <div style={ss.timerBadge}>{fmtTime(seconds)}</div>
      </div>

      {/* Chapter progress dots */}
      <div style={ss.chapterDots}>
        {book.chapters.map((_, i) => (
          <div key={i} style={ss.chDot(i === currentChapter)} />
        ))}
      </div>

      {/* Page area with parchment + 3D flip */}
      <div style={ss.pageOuter}>
        <div style={{ ...ss.pageInner, ...animStyle }}>
          <div style={ss.bindingShadow} />
          {currentPage?.type === 'title'
            ? <TitlePage book={book} />
            : currentPage?.type === 'content'
              ? <ContentPage page={currentPage} pageNumber={pageIndex} />
              : null
          }
        </div>
      </div>

      {/* Bottom bar: prev/next + page dots */}
      <div style={ss.readerBottomBar}>
        <button
          style={ss.navBtn(pageIndex === 0)}
          onClick={prevPage}
          disabled={pageIndex === 0}
        >‹</button>

        <div style={ss.pageDots}>
          {pages.slice(0, Math.min(pages.length, 9)).map((_, i) => (
            <div key={i} style={ss.pageDot(i === Math.min(pageIndex, 8))} />
          ))}
          {pages.length > 9 && (
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>…</span>
          )}
        </div>

        <button style={ss.navBtn(false)} onClick={nextPage}>›</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// LIBRARY VIEW
// ─────────────────────────────────────────────
function LibraryView({ onStartReading }) {
  const [genreFilter, setGenreFilter] = useState('All');
  const [modalBook, setModalBook] = useState(null);

  // Group books by language label
  const langGroups = useMemo(() => {
    const groups = {};
    BOOKS.forEach(b => {
      const lang = LANG_MAP[b.lang] || (b.lang ? b.lang.toUpperCase() : 'OTHER');
      if (!groups[lang]) groups[lang] = [];
      groups[lang].push(b);
    });
    return groups;
  }, []);

  function matchesGenre(book) {
    if (genreFilter === 'All') return true;
    const map = { 'Sci-Fi': 'Science Fiction', 'Thriller': 'Thriller', 'Romance': 'Romance' };
    return book.genre === genreFilter || book.genre === (map[genreFilter] || genreFilter);
  }

  return (
    <div style={ss.root}>
      {/* Genre filter pills */}
      <div style={ss.filterRow}>
        {GENRES.map(g => (
          <button key={g} style={ss.pill(genreFilter === g)} onClick={() => setGenreFilter(g)}>
            {g}
          </button>
        ))}
      </div>

      {/* Language carousels */}
      {Object.entries(langGroups).map(([lang, books]) => {
        const filtered = books.filter(matchesGenre);
        if (filtered.length === 0) return null;
        return (
          <div key={lang} style={ss.langSection}>
            <div style={ss.langLabel}>{lang}</div>
            <div style={ss.shelfRow}>
              {filtered.map(book => (
                <BookCard key={book.id} book={book} onOpen={setModalBook} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Back-cover lightbox */}
      {modalBook && (
        <BookModal
          book={modalBook}
          onClose={() => setModalBook(null)}
          onStartReading={(b) => { setModalBook(null); onStartReading(b); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ROOT EXPORT
// ─────────────────────────────────────────────
export default function ReadTab() {
  useEffect(() => { injectStyles(); }, []);

  const [activeBook, setActiveBook] = useState(null);

  if (activeBook) {
    return <ReaderView book={activeBook} onClose={() => setActiveBook(null)} />;
  }

  return <LibraryView onStartReading={setActiveBook} />;
}
